import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "../../services/authWrapper";
import useTranslator from "../../hooks/useTranslator";
import { toast } from "../../helper/common";
import Logo from "../../assets/img/skorLogo.svg";
import EnterOtpModal from "./enterOtpModal";
import CompanyProfileCompletedModal from "./CompanyProfileCompletedModal";
import "./existingProfileConsent.css";

type View = "intro" | "otp" | "complete" | "error";

const ExistingProfileConsent = () => {
  const { _t } = useTranslator();
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token") || "";
  const [view, setView] = useState<View>("intro");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [showResend, setShowResend] = useState(false);
  const [showTimer, setShowTimer] = useState(60);
  const [isProfileOutdated, setIsProfileOutdated] = useState(false);
  const [renewalToken, setRenewalToken] = useState("");
  const [continuationToken, setContinuationToken] = useState("");
  const [renewalLoading, setRenewalLoading] = useState(false);
  const completionSent = useRef(false);
  const timerRef = useRef<number | null>(null);
  const serverBase = (process.env.REACT_APP_SERVER_URL || "").replace(
    /\/$/,
    ""
  );
  const apiBase = serverBase.endsWith("/api/v1")
    ? serverBase
    : `${serverBase}/api/v1`;

  const headers = {
    "Accept-Language": localStorage.getItem("i18nextLng") || "en-us",
  };

  const notifyCancel = () => {
    window.parent.postMessage({ type: "TENANT_ONBOARDING_CANCELLED" }, "*");
  };

  const redirectToOnboarding = useCallback(
    (onboardingToken: string) => {
      const queryParams = new URLSearchParams(search);
      queryParams.set("token", onboardingToken);
      queryParams.set("usertype", "tenant");
      queryParams.set("is_verified", "true");
      queryParams.set("app_type", "skor-sdk");
      window.location.replace(`/redirect?${queryParams.toString()}`);
    },
    [search]
  );

  const startResendTimer = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    setShowResend(false);
    setShowTimer(60);
    timerRef.current = window.setInterval(() => {
      setShowTimer((seconds) => {
        if (seconds > 1) return seconds - 1;
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
        setShowResend(true);
        return 60;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    axiosInstance
      .post(`${apiBase}/tenants/account-status`, { token }, { headers })
      .then(({ data }) => {
        setEmail(data.data?.tenantEmail || "");
        setIsProfileOutdated(Boolean(data.data?.isProfileOutdated));
        if (
          data.data?.profile_completed &&
          !data.data?.requiresExistingTenantLogin &&
          !data.data?.isProfileOutdated
        ) {
          setView("complete");
        }
      })
      .catch((requestError) => {
        toast.error(
          requestError?.response?.data?.message ||
            _t("existingProfileVerificationLinkInvalid")
        );
        setView("error");
      })
      .finally(() => setCheckingToken(false));

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  const sendOtp = useCallback(async () => {
    setLoading(true);
    try {
      await axiosInstance.post(
        `${apiBase}/tenants/existing-profile/send-otp`,
        { token },
        { headers }
      );
      setView("otp");
      setOtp("");
      startResendTimer();
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("failedToSendOtp")
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, startResendTimer, token]);

  const verifyOtp = useCallback(
    async (event: any) => {
      event?.preventDefault?.();
      if (otp.length !== 6 || loading) return;
      setLoading(true);
      try {
        const { data } = await axiosInstance.post(
          `${apiBase}/tenants/existing-profile/verify-otp`,
          { token, otp },
          { headers }
        );
        if (data.data?.requires_onboarding && data.data?.onboarding_token) {
          setContinuationToken(data.data.onboarding_token);
          setIsProfileOutdated(false);
          setView("complete");
          return;
        }
        setIsProfileOutdated(Boolean(data.data?.is_profile_outdated));
        setRenewalToken(data.data?.renewal_token || "");
        setView("complete");
      } catch (requestError: any) {
        toast.error(
          requestError?.response?.data?.message ||
            _t("OTPDoesnotMatchesPleasetryagain")
        );
      } finally {
        setLoading(false);
      }
    },
    [apiBase, loading, otp, token]
  );

  const reinitiateOnboarding = useCallback(async () => {
    if (!renewalToken || renewalLoading) return;
    setRenewalLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/existing-profile/reinitiate-onboarding`,
        { renewal_token: renewalToken },
        { headers }
      );
      const onboardingToken = data.data?.token;
      if (!onboardingToken) throw new Error("Missing onboarding token");

      redirectToOnboarding(onboardingToken);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          _t("existingProfileVerificationLinkInvalid")
      );
      setRenewalLoading(false);
    }
  }, [apiBase, redirectToOnboarding, renewalLoading, renewalToken]);

  const continueOnboarding = useCallback(() => {
    if (continuationToken) redirectToOnboarding(continuationToken);
  }, [continuationToken, redirectToOnboarding]);

  useEffect(() => {
    if (
      view !== "complete" ||
      isProfileOutdated ||
      Boolean(continuationToken) ||
      completionSent.current
    ) {
      return;
    }
    completionSent.current = true;
    const timer = window.setTimeout(() => {
      window.parent.postMessage(
        {
          type: "TENANT_ONBOARDING_COMPLETED",
          data: { profile_completed: true },
        },
        "*"
      );
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [continuationToken, isProfileOutdated, view]);

  return (
    <main className="existing-consent-page">
      {view === "intro" && (
        <section className="existing-consent-card">
          <header className="existing-consent-brand">
            <img
              className="existing-consent-logo"
              src={Logo}
              alt="Skor"
            />
            <span className="existing-consent-domain">🔒 skor.io</span>
          </header>
          <div className="existing-consent-content">
            <h1>{_t("existingSkorProfileTitle")}</h1>
            <p>{_t("existingSkorProfileDescription")}</p>
            {email && <p className="existing-consent-email">{email}</p>}
            <button disabled={loading || checkingToken} onClick={sendOtp}>
              {checkingToken
                ? _t("loading")
                : loading
                ? _t("sendingCode")
                : _t("loginWithSkor")}
            </button>
          </div>
        </section>
      )}

      {view === "otp" && (
        <EnterOtpModal
          otp={otp}
          setOtp={setOtp}
          onClose={notifyCancel}
          showResend={showResend}
          showTimer={showTimer}
          sendOtpFun={sendOtp}
          verifyOTPHandler={verifyOtp}
          verifyOtpLoading={loading}
          email={email}
        />
      )}

      {view === "complete" && (
        <CompanyProfileCompletedModal
          iframeProfile
          titleKey="profileLinked"
          actionLabel={
            isProfileOutdated
              ? _t("profileIsOutdatedRedoOnboarding")
              : continuationToken
              ? _t("continueOnboarding")
              : undefined
          }
          onAction={
            isProfileOutdated
              ? reinitiateOnboarding
              : continuationToken
              ? continueOnboarding
              : undefined
          }
          actionLoading={renewalLoading}
        />
      )}

      {view === "error" && (
        <section className="existing-consent-card">
          <div className="existing-consent-content">
            <h1>{_t("unableToContinue")}</h1>
            <p>{_t("existingProfileVerificationLinkInvalid")}</p>
          </div>
        </section>
      )}
    </main>
  );
};

export default ExistingProfileConsent;
